import { useEffect, useRef } from "react";

import { Box, Image } from "@chakra-ui/react";
import {
  GoalSavingsStatusEnum,
  IGoalSavings,
  IIndividualGoalSavingsPayload,
  TSavingsFrequency,
} from "api/individual-savings/types";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
import {
  DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS,
  FIRST_INDEX,
  goalSavingsColumns,
} from "features/individuals-savings/consts";
import { EmptyImagePlaceholder } from "features/individuals/individuals/individuals-list-table/EmptyImagePlaceholder";
import { individualTableColumnCss } from "features/individuals/individuals/styles";
import { useTableActions } from "hooks/useTableActions";
import { useSearchParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  capitalizeName,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination, { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { IPaginationData } from "ui/pagination/types";
import { Status } from "ui/status";
import { StatusUiColorEnum, TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";

import { IndividualsSavingsStats } from "./IndividualsSavingsStats";
import { TableHeaderCTAs } from "./TableHeaderCTAs";
import { MoreQuickActions } from "./more-quick-actions";

export const getTransationStatus = (status: string): TStatus => {
  let Savingstatus = status;

  if (Savingstatus === GoalSavingsStatusEnum.ACTIVE) {
    Savingstatus = StatusUiColorEnum.SUCCESS;
  } else if (Savingstatus === GoalSavingsStatusEnum.STOPPED) {
    Savingstatus = StatusUiColorEnum.ERROR;
  } else if (Savingstatus === GoalSavingsStatusEnum.PAUSED) {
    Savingstatus = StatusUiColorEnum.ORANGE;
  } else if (
    Savingstatus === GoalSavingsStatusEnum.COMPLETED ||
    Savingstatus === GoalSavingsStatusEnum.DISBURSED ||
    Savingstatus === GoalSavingsStatusEnum.MATURED
  ) {
    Savingstatus = StatusUiColorEnum.BLUE;
  } else {
    Savingstatus = StatusUiColorEnum.DISABLED;
  }

  return Savingstatus as TStatus;
};

export const getTransationStatusText = (status: GoalSavingsStatusEnum) => {
  let Savingstatus: string = status;

  if (Savingstatus === GoalSavingsStatusEnum.ACTIVE) {
    Savingstatus = "Active";
  } else if (
    Savingstatus === GoalSavingsStatusEnum.COMPLETED ||
    Savingstatus === GoalSavingsStatusEnum.DISBURSED ||
    Savingstatus === GoalSavingsStatusEnum.MATURED
  ) {
    Savingstatus = "Completed";
  } else if (
    Savingstatus === GoalSavingsStatusEnum.DELETED ||
    Savingstatus === GoalSavingsStatusEnum.STOPPED
  ) {
    Savingstatus = "Stopped";
  } else {
    Savingstatus = capitalizeFirstLetter(Savingstatus);
  }

  return Savingstatus;
};

const GoalSavings = () => {
  const {
    isLoadingIndividualGoalSavings,
    handleGetIndividualGoalSavings,
    isLoadingIndividualGoalSavingsStats,
    individualGoalSavingsResult,
    handleGetIndividualGoalSavingsStats,
  } = useIndividualsSavings();
  const { handleRefreshTable, isRefreshing } = useTableActions({
    isLoadingTable: isLoadingIndividualGoalSavings,
    onTableRefresh: () => {
      void handleGetIndividualGoalSavingsStats(
        {},
        {
          isRefetching: true,
        }
      );
    },
  });

  const tableRef = useRef<HTMLTableElement | null>(null);
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const status = searchParams.get("status");
  const frequency = searchParams.get("frequency");
  const search = searchParams.get("search");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const tableRefreshCount = searchParams.get("tableRefreshCount");

  useEffect(() => {
    const payload: IIndividualGoalSavingsPayload = {
      page: String(page || DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page),
      limit: String(
        limit || DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (frequency) {
      const transactionFrequency = frequency.split(",");
      const filteredFrequency = transactionFrequency
        .filter((freq) => freq !== "all")
        .join(",");
      payload["frequency"] = filteredFrequency as TSavingsFrequency;
    }

    if (status) {
      const transactionStatus = status.split(",");
      if (!transactionStatus.includes("all")) {
        payload["status"] = transactionStatus.join(
          ","
        ) as GoalSavingsStatusEnum;
      }
    }
    if (search) {
      payload["search"] = search;
      payload["searchBy"] = "goal";
    }

    void handleGetIndividualGoalSavings(payload, {
      isRefetching: false,
    });
  }, [
    page,
    limit,
    startDate,
    endDate,
    search,
    frequency,
    status,
    filterRetryCount,
    tableRefreshCount,
  ]);

  const { data, ...paginationData } = individualGoalSavingsResult || {
    data: [],
    ...DEFAULT_PAGINATION_DATA,
  };

  const formattedData = data?.map((saving: IGoalSavings) => {
    const {
      id,
      email,
      endsAt: maturityDate,
      target: savingsAmount,
      goal: savingsName,
      status,
      firstName,
      lastName,
      totalInterest: interestEarned,
      totalAmountSaved: savedAmount,
      source,
    } = saving as IGoalSavings;
    const fullName =
      firstName && lastName && `${capitalizeName(firstName, lastName)}`;

    return {
      id,
      "Saver's Name": (
        <Box className="flex gap-3 items-center">
          {!id ? (
            <Image
              className="w-[32px] h-[32px] rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              src={"profileImage"}
              alt={`${fullName}'s image`}
            />
          ) : (
            <EmptyImagePlaceholder
              className="w-[32px] h-[32px] uppercase text-xs font-semibold rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              initial={fullName?.[FIRST_INDEX]}
              index={id as string}
            />
          )}
          <Box css={individualTableColumnCss}>
            <Box className="text-sm leading-[14px] tracking-[0%] mb-1 font-semibold text-[#09090B]">
              {fullName}
            </Box>
            <Box className="font-medium text-xs tracking-[0%] leading-[12px] text-[#718096]">
              {email}
            </Box>
          </Box>
        </Box>
      ),
      "Goal Name": (
        <Box css={individualTableColumnCss}>{savingsName || "N/A"}</Box>
      ),
      "Goal Amount": (
        <Box css={individualTableColumnCss}>
          {savingsAmount === null
            ? "N/A"
            : handleAmountFormat({
                value: String(savingsAmount / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Amount Saved": (
        <Box css={individualTableColumnCss}>
          {savedAmount === null
            ? "N/A"
            : handleAmountFormat({
                value: String(savedAmount / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Interest Earned": (
        <Box css={individualTableColumnCss}>
          {interestEarned === null
            ? "N/A"
            : handleAmountFormat({
                value: String(interestEarned / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Maturity Date": (
        <Box css={individualTableColumnCss}>
          {maturityDate ? formatDateToDDMMMYYYY(maturityDate) : "N/A"}
        </Box>
      ),
      Source: (
        <Box css={individualTableColumnCss}>
          {(source && (source === "wallet" ? "Account" : "Card")) || "N/A"}
        </Box>
      ),
      Status: (
        <Status
          status={getTransationStatus(status)}
          text={getTransationStatusText(status)}
          className="w-fit"
        />
      ),
      "": <MoreQuickActions individualsSavingDetails={saving} />,
    };
  });

  return (
    <div>
      <Box>
        <IndividualsSavingsStats
          isLoadingIndividualsStats={isLoadingIndividualGoalSavingsStats}
        />
      </Box>
      <TableWrapper className="!p-0">
        <TableHeaderCTAs
          handleGetTransactions={handleGetIndividualGoalSavings}
          isLoadingTransactions={isLoadingIndividualGoalSavings}
          isRefreshing={isRefreshing}
          handleRefreshTable={handleRefreshTable}
        />
        <>
          <Table
            columns={goalSavingsColumns}
            data={formattedData || []}
            tableRef={tableRef}
            hasCheckbox
            isLoading={isLoadingIndividualGoalSavings && !isRefreshing}
          />
          <Pagination
            paginationData={paginationData as IPaginationData}
            data={data}
            defaultPaginationParams={
              DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS
            }
            tableRef={tableRef}
            isLoadingTable={isLoadingIndividualGoalSavings}
          />
        </>
      </TableWrapper>
    </div>
  );
};

export default GoalSavings;
